import { YES_NO, applicantSection, feeInfo, otherAttachments, replySection } from "modules/ebiForms/common";
import { householdFields, socialApplicantRoleSection, socialRoleAttachments } from "modules/ebiForms/definitions/sharedSocial";
import { EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * C3. Wniosek o przyznanie usług opiekuńczych lub specjalistycznych usług opiekuńczych w miejscu zamieszkania — art. 50,
 * 102, 106 ust. 4 i 107 ustawy z 12.03.2004 r. o pomocy społecznej; rozporządzenie MPS z 22.09.2005 r. w sprawie
 * specjalistycznych usług opiekuńczych; warunki i odpłatność — uchwała rady gminy (art. 50 ust. 6). Brak wzoru
 * ogólnopolskiego; układ jak wzór MGOPS Pleszew
 * (https://bip-mgops.pleszew.pl/download/Wniosek-o-przyznanie-uslug-opiekunczych-1773917049.pdf, pobrany 22.10.2026: dane,
 * „z powodu…”, zgoda na wywiad i ustalenie odpłatności, gospodarstwo domowe, załączniki: decyzja ZUS/KRUS, orzeczenie,
 * zaświadczenie lekarskie). Rodzaj usług jest wariantem wniosku w BE. Oświadczenie o stanie majątkowym — nieobowiązkowe
 * w EBI (katalog: „[zawsze]”, ale to druk z rozporządzenia o wywiadzie, składany zwykle w trakcie wywiadu, jak w C1) —
 * do potwierdzenia z ośrodkiem.
 */
const C3: IEbiFormDefinition = {
  code: "C3",
  version: 1,
  title: "Wniosek o przyznanie usług opiekuńczych",
  category: EEbiFormCategory.Social,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Usługi opiekuńcze albo specjalistyczne usługi opiekuńcze w miejscu zamieszkania (pomoc w codziennych czynnościach, pielęgnacja).",
  keywords: ["usługi opiekuńcze", "specjalistyczne usługi opiekuńcze", "opiekunka", "pomoc w domu", "pomoc społeczna", "ops"],
  organ: "Wójt / Burmistrz / Prezydent Miasta — w praktyce kierownik ośrodka pomocy społecznej (centrum usług społecznych) z upoważnienia",
  legalBasis: "art. 50 i art. 102 ustawy z dnia 12 marca 2004 r. o pomocy społecznej",
  request: "Na podstawie art. 50 ustawy o pomocy społecznej zwracam się z prośbą o przyznanie pomocy w formie usług wskazanych we wniosku.",
  feeLabel: "wg stawek",
  fee: "Bez opłaty skarbowej. Odpłatność za usługi (stawka za godzinę i zwolnienia) określa uchwała rady gminy — ustala ją decyzja.",
  deadlineLabel: "do miesiąca",
  deadline: "Decyzja po rodzinnym wywiadzie środowiskowym, do miesiąca (art. 35 KPA).",
  variantField: "rodzajUslug",
  variants: {
    opiekuncze: { title: "Wniosek o przyznanie usług opiekuńczych" },
    specjalistyczne: { title: "Wniosek o przyznanie specjalistycznych usług opiekuńczych" },
    specjalistyczneZaburzenia: { title: "Wniosek o przyznanie specjalistycznych usług opiekuńczych dla osób z zaburzeniami psychicznymi" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj usług",
      fields: [
        {
          code: "rodzajUslug",
          type: "radio",
          label: "Wnoszę o przyznanie",
          required: true,
          defaultValue: "opiekuncze",
          options: [
            {
              value: "opiekuncze",
              label: "usług opiekuńczych — pomoc w codziennych potrzebach życiowych, opieka higieniczna, zalecona przez lekarza pielęgnacja, kontakty z otoczeniem",
            },
            { value: "specjalistyczne", label: "specjalistycznych usług opiekuńczych — dostosowanych do szczególnych potrzeb wynikających z rodzaju schorzenia lub niepełnosprawności" },
            { value: "specjalistyczneZaburzenia", label: "specjalistycznych usług opiekuńczych dla osób z zaburzeniami psychicznymi" },
          ],
        },
      ],
    },
    applicantSection(),
    socialApplicantRoleSection(),
    {
      code: "uslugi",
      title: "Uzasadnienie",
      fields: [
        {
          code: "uzasadnienie",
          type: "textarea",
          label: "Proszę o przyznanie usług z powodu",
          required: true,
          maxLength: 5000,
          hint: "Np. wiek, choroba, niepełnosprawność; brak osób, które mogą zapewnić pomoc.",
        },
        {
          code: "zakres",
          type: "textarea",
          label: "W jakich czynnościach potrzebna jest pomoc",
          maxLength: 2000,
          hint: "Nieobowiązkowo, np. zakupy, przygotowanie posiłków, sprzątanie, higiena. Zakres, okres i miejsce usług ustala ośrodek (art. 50 ust. 5).",
        },
        ...householdFields(),
        {
          code: "orzeczenie",
          type: "radio",
          label: "Czy osoba, której dotyczy wniosek, ma orzeczenie o niepełnosprawności lub o stopniu niepełnosprawności?",
          required: true,
          options: YES_NO,
        },
        {
          code: "zgodaWywiad",
          type: "declaration",
          label:
            "Wyrażam zgodę na przeprowadzenie przez pracownika socjalnego rodzinnego wywiadu środowiskowego i ustalenie na jego podstawie zakresu usług, jak również wysokości i trybu pobierania odpłatności za usługi.",
          required: true,
        },
        feeInfo(
          "infoOdplatnosc",
          "Usługi są odpłatne według uchwały rady gminy (stawka za godzinę i zasady zwolnienia zależą od dochodu). Wysokość odpłatności ośrodek ustala w decyzji po wywiadzie.",
        ),
      ],
    },
    replySection(),
  ],
  attachments: [
    ...socialRoleAttachments(),
    {
      code: "zaswiadczenieLekarskie",
      label: "Zaświadczenie lekarskie o stanie zdrowia i konieczności świadczenia usług w miejscu zamieszkania",
      hint: "Aktualne, z zalecanym zakresem czynności — na druku ośrodka lub dowolnym.",
      required: true,
    },
    {
      code: "zaswiadczeniePsychiatry",
      label: "Zaświadczenie lekarza psychiatry lub neurologa z zalecanym rodzajem i wymiarem usług",
      visibleWhen: { field: "rodzajUslug", equals: "specjalistyczneZaburzenia" },
      required: true,
    },
    {
      code: "dochody",
      label: "Dokumenty o dochodach (np. decyzja ZUS lub KRUS, zaświadczenie o wynagrodzeniu)",
      hint: "Dochód jest podstawą ustalenia odpłatności — dla osoby zainteresowanej i osób we wspólnym gospodarstwie domowym.",
      required: true,
      multiple: true,
    },
    {
      code: "orzeczenieNiepelnosprawnosci",
      label: "Orzeczenie o niepełnosprawności lub o stopniu niepełnosprawności",
      visibleWhen: { field: "orzeczenie", equals: "tak" },
      required: true,
    },
    {
      code: "oswiadczenieMajatkowe",
      label: "Oświadczenie o stanie majątkowym",
      hint: "Nieobowiązkowo przy wniosku — wzór z rozporządzenia o rodzinnym wywiadzie środowiskowym; zwykle składa się je pracownikowi socjalnemu w trakcie wywiadu.",
    },
    {
      code: "oswiadczeniaRodziny",
      label: "Oświadczenia pełnoletnich członków rodziny o braku możliwości sprawowania opieki",
      hint: "Nieobowiązkowo — część ośrodków o nie prosi.",
      multiple: true,
    },
    otherAttachments(),
  ],
};

export default C3;
